import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Layout,
  Button,
  Upload,
  Input,
  Select,
  Checkbox,
  Tag,
  Space,
  Tooltip,
  message,
  Spin,
  Typography,
  Empty,
  Divider,
  Popover,
  Image,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  StarFilled,
  StarOutlined,
  ArrowLeftOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  LoadingOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import request from '@/utils/request';
import { useAuthStore } from '@/stores/auth.store';
import {
  resolveImageCostWithDefaults,
  usdToRmbCentsCeil,
  type ImageModelDTO,
  type ImageInputSchema,
  type ImageInputField,
  type ImagePricing,
} from '@zero/shared';
import { formatCents, formatDate } from '@/utils/format';
import { thumb } from '@/utils/image';
import RequestPreviewModal from '@/components/RequestPreviewModal';

const { Sider, Content } = Layout;

interface ExperimentDraft {
  referenceIds: number[];
  userInputIds: number[];
  prompt: string;
  selectedSlugs: string[];
  perModelInputs: Record<string, Record<string, unknown>>;
  runLabel: string;
}

function readDraft(key: string): Partial<ExperimentDraft> | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

function writeDraft(key: string, draft: ExperimentDraft): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // ignore quota / privacy-mode errors
  }
}

interface ResourceRow {
  id: number;
  imageId: number;
  label: string | null;
  kind: string;
  image: { id: number; accessUrl: string; width: number | null; height: number | null };
}

interface RecordRow {
  id: number;
  runBatchId: string;
  runLabel: string | null;
  modelSlug: string;
  status: 'pending' | 'running' | 'success' | 'failed' | 'timeout';
  prompt: string | null;
  inputsJson: Record<string, unknown>;
  referenceResourceIds: number[];
  userInputResourceIds: number[];
  outputImageIds: number[];
  chargedCents: number | null;
  costUsd: number | null;
  markedAsCover: boolean;
  errorMessage: string | null;
  createdAt: string;
}

const ExperimentWorkbenchPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const experimentId = Number(id);
  const refreshBalances = useAuthStore((s) => s.refreshBalances);

  const [experiment, setExperiment] = useState<{
    id: number;
    name: string;
    coverImageId: number | null;
  } | null>(null);
  const [models, setModels] = useState<ImageModelDTO[]>([]);
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [records, setRecords] = useState<RecordRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state — restored from localStorage so a refresh preserves the draft.
  // v2 prefix invalidates pre-fix drafts that may contain stale state leaked
  // from another experiment by an older version of the auto-save effect.
  const draftKey = `experiment-draft-v2-${experimentId}`;
  const [referenceIds, setReferenceIds] = useState<number[]>(
    () => readDraft(draftKey)?.referenceIds ?? [],
  );
  const [userInputIds, setUserInputIds] = useState<number[]>(
    () => readDraft(draftKey)?.userInputIds ?? [],
  );
  const [prompt, setPrompt] = useState(() => readDraft(draftKey)?.prompt ?? '');
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>(
    () => readDraft(draftKey)?.selectedSlugs ?? [],
  );
  const [perModelInputs, setPerModelInputs] = useState<Record<string, Record<string, unknown>>>(
    () => readDraft(draftKey)?.perModelInputs ?? {},
  );
  const [runLabel, setRunLabel] = useState(() => readDraft(draftKey)?.runLabel ?? '');
  const [previewOpen, setPreviewOpen] = useState(false);

  // Persist draft on change.
  useEffect(() => {
    writeDraft(draftKey, {
      referenceIds,
      userInputIds,
      prompt,
      selectedSlugs,
      perModelInputs,
      runLabel,
    });
  }, [draftKey, referenceIds, userInputIds, prompt, selectedSlugs, perModelInputs, runLabel]);

  // Image URL cache for outputs (avoid N round-trips per record render)
  const [outputUrlMap, setOutputUrlMap] = useState<Map<number, string>>(new Map());
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [exchangeRate] = useState(7.3);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [exp, modelsRes, resourcesRes, recordsRes] = await Promise.all([
        request.get<{ id: number; name: string; coverImageId: number | null }>(
          `/creator/experiments/${experimentId}`,
        ),
        request.get<ImageModelDTO[]>('/creator/image-models'),
        request.get<{ items: ResourceRow[] }>('/creator/resources?pageSize=100'),
        request.get<{ items: RecordRow[] }>(
          `/creator/experiments/${experimentId}/records?pageSize=200`,
        ),
      ]);
      setExperiment(exp.data as unknown as { id: number; name: string; coverImageId: number | null });
      setModels(modelsRes.data as unknown as ImageModelDTO[]);
      setResources((resourcesRes.data as unknown as { items: ResourceRow[] }).items);
      const recList = (recordsRes.data as unknown as { items: RecordRow[] }).items;
      setRecords(recList);
      // Batch-fetch output image URLs (+ cover).
      const expData = exp.data as unknown as { coverImageId: number | null };
      const needIds = new Set<number>(recList.flatMap((r) => r.outputImageIds));
      if (expData.coverImageId) needIds.add(expData.coverImageId);
      if (needIds.size > 0) {
        const res = await request.get<{ items: { id: number; accessUrl: string }[] }>(
          `/creator/images?ids=${Array.from(needIds).join(',')}`,
        );
        const map = new Map<number, string>();
        for (const img of (res.data as unknown as { items: { id: number; accessUrl: string }[] }).items) {
          map.set(img.id, img.accessUrl);
        }
        setOutputUrlMap(map);
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [experimentId]);

  useEffect(() => {
    load();
  }, [load]);

  // After initial load, drop any restored draft ids that reference resources
  // or models no longer available.
  useEffect(() => {
    if (loading) return;
    const validResourceIds = new Set(resources.map((r) => r.id));
    setReferenceIds((prev) => {
      const next = prev.filter((id) => validResourceIds.has(id));
      return next.length === prev.length ? prev : next;
    });
    setUserInputIds((prev) => {
      const next = prev.filter((id) => validResourceIds.has(id));
      return next.length === prev.length ? prev : next;
    });
    const validSlugs = new Set(models.map((m) => m.slug));
    setSelectedSlugs((prev) => {
      const next = prev.filter((s) => validSlugs.has(s));
      return next.length === prev.length ? prev : next;
    });
    setPerModelInputs((prev) => {
      const next: typeof prev = {};
      let changed = false;
      for (const [slug, inputs] of Object.entries(prev)) {
        if (validSlugs.has(slug)) next[slug] = inputs;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [loading, resources, models]);

  const uploadProps = {
    name: 'file',
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }: any) => {
      try {
        const realFile: File = file;
        const presign = await request.post<{ uploadUrl: string; cosKey: string }>('/cos/upload-url', {
          filename: realFile.name,
          contentType: realFile.type,
        });
        const presignData = presign.data as unknown as { uploadUrl: string; cosKey: string };
        await fetch(presignData.uploadUrl, {
          method: 'PUT',
          body: realFile,
          headers: { 'Content-Type': realFile.type },
        });
        const confirm = await request.post<{ id: number }>('/cos/confirm-upload', {
          cosKey: presignData.cosKey,
          size: realFile.size,
          mimeType: realFile.type,
        });
        const confirmData = confirm.data as unknown as { id: number };
        const res = await request.post<ResourceRow>('/creator/resources', {
          imageId: confirmData.id,
          label: realFile.name,
        });
        const row = res.data as unknown as ResourceRow;
        setResources((prev) => [row, ...prev]);
        onSuccess?.(row);
        message.success('上传成功');
      } catch (e) {
        onError?.(e as Error);
        message.error(e instanceof Error ? e.message : '上传失败');
      }
    },
  };

  const removeResource = async (resourceId: number) => {
    try {
      await request.delete(`/creator/resources/${resourceId}`);
      setResources((prev) => prev.filter((r) => r.id !== resourceId));
      setReferenceIds((prev) => prev.filter((id) => id !== resourceId));
      setUserInputIds((prev) => prev.filter((id) => id !== resourceId));
    } catch (e) {
      message.error(e instanceof Error ? e.message : '删除失败');
    }
  };

  const refResources = useMemo(
    () => referenceIds.map((i) => resources.find((r) => r.id === i)).filter(Boolean) as ResourceRow[],
    [referenceIds, resources],
  );
  const userInputResources = useMemo(
    () => userInputIds.map((i) => resources.find((r) => r.id === i)).filter(Boolean) as ResourceRow[],
    [userInputIds, resources],
  );
  const resourceMap = useMemo(() => {
    const m = new Map<number, ResourceRow>();
    for (const r of resources) m.set(r.id, r);
    return m;
  }, [resources]);

  const perModelCost = useMemo(() => {
    const out: Array<{ model: ImageModelDTO; costUsd: number; rmbCents: number }> = [];
    for (const slug of selectedSlugs) {
      const model = models.find((m) => m.slug === slug);
      if (!model) continue;
      const inputs = { ...(perModelInputs[slug] ?? {}) } as Record<string, unknown>;
      if (prompt && !('prompt' in inputs)) inputs.prompt = prompt;
      const cost = resolveImageCostWithDefaults(
        model.inputSchema as ImageInputSchema,
        model.pricing as ImagePricing,
        inputs,
      );
      const rmbCents = usdToRmbCentsCeil(cost.costUsd, exchangeRate);
      out.push({ model, costUsd: cost.costUsd, rmbCents });
    }
    return out;
  }, [selectedSlugs, models, perModelInputs, prompt, exchangeRate]);

  const totalRmbCents = perModelCost.reduce((s, m) => s + m.rmbCents, 0);

  /** Refetch records only (lighter than full load) for polling. */
  const refreshRecords = useCallback(async () => {
    try {
      const res = await request.get<{ items: RecordRow[] }>(
        `/creator/experiments/${experimentId}/records?pageSize=200`,
      );
      const items = (res.data as unknown as { items: RecordRow[] }).items;
      setRecords(items);
      // Populate URL map for any new output image ids not yet loaded.
      const needIds = new Set<number>();
      for (const r of items) {
        for (const id of r.outputImageIds) {
          if (!outputUrlMap.has(id)) needIds.add(id);
        }
      }
      if (needIds.size > 0) {
        const imgRes = await request.get<{ items: { id: number; accessUrl: string }[] }>(
          `/creator/images?ids=${Array.from(needIds).join(',')}`,
        );
        const newMap = new Map(outputUrlMap);
        for (const img of (imgRes.data as unknown as { items: { id: number; accessUrl: string }[] }).items) {
          newMap.set(img.id, img.accessUrl);
        }
        setOutputUrlMap(newMap);
      }
      return items;
    } catch {
      return null;
    }
  }, [experimentId, outputUrlMap]);

  // Poll while any record is pending or running. Auto-stops when all settle.
  useEffect(() => {
    const hasActive = records.some((r) => r.status === 'pending' || r.status === 'running');
    if (!hasActive) {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }
    if (pollTimerRef.current) return;
    const tick = async () => {
      const items = await refreshRecords();
      if (items && items.some((r) => r.status === 'pending' || r.status === 'running')) {
        pollTimerRef.current = setTimeout(tick, 3000);
      } else {
        pollTimerRef.current = null;
        await refreshBalances();
      }
    };
    pollTimerRef.current = setTimeout(tick, 3000);
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [records, refreshRecords, refreshBalances]);

  const onSubmit = async () => {
    if (selectedSlugs.length === 0) {
      message.warning('请至少勾选一个模型');
      return;
    }
    setSubmitting(true);
    try {
      const res = await request.post<{ records: RecordRow[] }>(
        `/creator/experiments/${experimentId}/records`,
        {
          runLabel: runLabel.trim() || undefined,
          prompt,
          referenceResourceIds: referenceIds,
          userInputResourceIds: userInputIds,
          perModel: selectedSlugs.map((slug) => ({
            modelSlug: slug,
            inputs: perModelInputs[slug] ?? {},
          })),
        },
      );
      const data = res.data as unknown as { records: RecordRow[] };
      // Optimistic insert — server returned pending rows immediately; polling
      // will upgrade them to success/failed.
      setRecords((prev) => [...data.records, ...prev]);
      message.success('已提交，正在后台生成，可继续其它操作');
      setRunLabel('');
      setPreviewOpen(false);
      await refreshBalances();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteRecord = async (recId: number) => {
    try {
      await request.delete(`/creator/records/${recId}`);
      setRecords((prev) => prev.filter((r) => r.id !== recId));
    } catch (e) {
      message.error(e instanceof Error ? e.message : '删除失败');
    }
  };

  const markImageCover = async (imageId: number) => {
    try {
      const res = await request.post<{ coverImageId: number | null }>(
        `/creator/experiments/${experimentId}/set-cover-image`,
        { imageId },
      );
      const data = res.data as unknown as { coverImageId: number | null };
      setExperiment((prev) => (prev ? { ...prev, coverImageId: data.coverImageId } : prev));
      message.success('已设为实验台封面');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '操作失败');
    }
  };

  const sortedRecords = useMemo<RecordRow[]>(
    () => [...records].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [records],
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin />
      </div>
    );
  }

  // Drag-drop handlers — attach to DropSlot / thumbnails
  const onDragStart = (e: React.DragEvent, resId: number) => {
    e.dataTransfer.setData('text/resource-id', String(resId));
    e.dataTransfer.effectAllowed = 'copy';
    // Use the thumbnail element itself as the drag ghost so we don't get the
    // full-resolution image floating under the cursor.
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    e.dataTransfer.setDragImage(target, rect.width / 2, rect.height / 2);
  };
  const dropInto = (kind: 'reference' | 'user_input', e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('is-over');
    const id = Number(e.dataTransfer.getData('text/resource-id'));
    if (!Number.isFinite(id) || id <= 0) return;
    if (kind === 'reference') {
      setReferenceIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    } else {
      setUserInputIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
  };

  return (
    <Layout
      style={{ background: 'transparent', height: 'calc(100vh - 112px)' }}
    >
      {/* Left column: params */}
      <Sider width={360} theme="light" style={{ background: 'transparent' }}>
        <div className="wb-column">
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/experiments')}>
              返回
            </Button>
            <Typography.Text
              strong
              style={{ fontSize: 16 }}
              editable={{
                tooltip: '点击改名',
                onChange: async (next) => {
                  if (!experiment) return;
                  const trimmed = next.trim();
                  if (!trimmed || trimmed === experiment.name) return;
                  try {
                    const res = await request.patch<{ id: number; name: string; coverImageId: number | null }>(
                      `/creator/experiments/${experiment.id}`,
                      { name: trimmed },
                    );
                    const data = res.data as unknown as {
                      id: number;
                      name: string;
                      coverImageId: number | null;
                    };
                    setExperiment((prev) => (prev ? { ...prev, name: data.name } : prev));
                  } catch (e) {
                    message.error(e instanceof Error ? e.message : '改名失败');
                  }
                },
              }}
            >
              {experiment?.name}
            </Typography.Text>
          </Space>

          <DropSlot
            title="参考图（风格 / 示例）"
            count={refResources.length}
            items={refResources}
            onDrop={(e) => dropInto('reference', e)}
            onRemove={(id) => setReferenceIds((prev) => prev.filter((i) => i !== id))}
          />

          <DropSlot
            title="用户输入图（主体素材）"
            count={userInputResources.length}
            items={userInputResources}
            onDrop={(e) => dropInto('user_input', e)}
            onRemove={(id) => setUserInputIds((prev) => prev.filter((i) => i !== id))}
          />

          <div>
            <Typography.Text strong style={{ fontSize: 13 }}>
              指令 / Prompt
            </Typography.Text>
            <Input.TextArea
              rows={3}
              style={{ marginTop: 6 }}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="比如：把这张照片变成油画风格"
            />
          </div>

          <div>
            <Typography.Text strong style={{ fontSize: 13 }}>
              模型勾选（多选即批量执行）
            </Typography.Text>
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {models.map((m) => (
                <ModelItem
                  key={m.slug}
                  model={m}
                  checked={selectedSlugs.includes(m.slug)}
                  inputs={perModelInputs[m.slug] ?? {}}
                  onCheck={(checked) => {
                    setSelectedSlugs((prev) =>
                      checked ? [...prev, m.slug] : prev.filter((s) => s !== m.slug),
                    );
                    if (checked) {
                      setPerModelInputs((prev) => {
                        if (prev[m.slug]) return prev;
                        const defaults: Record<string, unknown> = {};
                        for (const f of (m.inputSchema as ImageInputSchema).fields) {
                          if (f.type === 'image_url' || f.type === 'image_urls') continue;
                          if (f.key === 'prompt') continue;
                          if (f.defaultValue !== undefined) defaults[f.key] = f.defaultValue;
                        }
                        return { ...prev, [m.slug]: defaults };
                      });
                    } else {
                      setPerModelInputs((prev) => {
                        if (!(m.slug in prev)) return prev;
                        const { [m.slug]: _removed, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  onChangeInput={(next) => {
                    setPerModelInputs((prev) => ({ ...prev, [m.slug]: next }));
                  }}
                  exchangeRate={exchangeRate}
                  prompt={prompt}
                />
              ))}
            </div>
          </div>

          {/* Footer: run label + preview trigger + generate CTA */}
          <div
            style={{
              marginTop: 'auto',
              paddingTop: 12,
              borderTop: '1px solid #F0EBDE',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <Input
              size="small"
              value={runLabel}
              onChange={(e) => setRunLabel(e.target.value)}
              placeholder="执行名（留空自动生成）"
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 12,
                color: '#64748B',
              }}
            >
              <span>
                {selectedSlugs.length} 个模型 · 合计{' '}
                <span style={{ color: '#D97706', fontWeight: 500 }}>
                  {formatCents(totalRmbCents)}
                </span>
              </span>
              <Button
                size="small"
                type="text"
                icon={<EyeOutlined />}
                onClick={() => setPreviewOpen(true)}
              >
                预览
              </Button>
            </div>
            <Button
              type="primary"
              size="large"
              icon={<ThunderboltOutlined />}
              block
              disabled={selectedSlugs.length === 0}
              loading={submitting}
              onClick={onSubmit}
            >
              {submitting ? '提交中' : `生成 · ${formatCents(totalRmbCents)}`}
            </Button>
          </div>
        </div>
      </Sider>

      {/* Middle column: live preview (fixed) + scrollable records */}
      <Content
        style={{
          marginLeft: 16,
          background: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div className="wb-column" style={{ flex: 1 }}>
          <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 12 }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              实验记录（{records.length}）
            </Typography.Title>
            {experiment?.coverImageId && outputUrlMap.has(experiment.coverImageId) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12,
                  color: '#64748B',
                }}
              >
                当前封面：
                <Image
                  src={thumb(outputUrlMap.get(experiment.coverImageId)!, 48)}
                  preview={{ src: outputUrlMap.get(experiment.coverImageId)! }}
                  width={40}
                  height={40}
                  style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #EEE7D4' }}
                />
              </div>
            )}
          </Space>
          {sortedRecords.length === 0 ? (
            <Empty className="brand-empty" description="还没有记录，在左边提交一次试试吧" />
          ) : (
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {sortedRecords.map((r) => (
                <RecordCard
                  key={r.id}
                  row={r}
                  urlMap={outputUrlMap}
                  resourceMap={resourceMap}
                  onDelete={() => deleteRecord(r.id)}
                  onMarkCover={(imgId) => markImageCover(imgId)}
                  coverImageId={experiment?.coverImageId ?? null}
                />
              ))}
            </Space>
          )}
        </div>
      </Content>

      {/* Right column: resource library (compact dense grid) */}
      <Sider
        width={232}
        theme="light"
        style={{ background: 'transparent', marginLeft: 16 }}
      >
        <div className="wb-column">
          <Upload {...uploadProps} accept="image/*">
            <Button icon={<UploadOutlined />} type="primary" block size="small">
              上传资源
            </Button>
          </Upload>
          <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: 'block' }}>
            拖拽缩略图到左侧的"参考图 / 用户输入"槽位
          </Typography.Text>
          <Divider style={{ margin: '10px 0' }} />
          {resources.length === 0 ? (
            <Empty className="brand-empty" description="资源库为空" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Image.PreviewGroup>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 4,
              }}
            >
              {resources.map((r) => {
                const inRef = referenceIds.includes(r.id);
                const inUser = userInputIds.includes(r.id);
                return (
                  <div
                    key={r.id}
                    className={`resource-thumb ${inRef || inUser ? 'is-selected' : ''}`}
                    draggable
                    onDragStart={(e) => onDragStart(e, r.id)}
                    title={r.label ?? ''}
                  >
                    <Image
                      src={thumb(r.image.accessUrl, 120)}
                      preview={{ src: r.image.accessUrl, mask: null }}
                      alt={r.label ?? ''}
                      width="100%"
                      draggable={false}
                      style={{ aspectRatio: '1/1', objectFit: 'cover', display: 'block' }}
                    />
                    <Button
                      className="resource-thumb__del"
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined style={{ fontSize: 10 }} />}
                      style={{ width: 18, height: 18, minWidth: 18, padding: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeResource(r.id);
                      }}
                    />
                    {(inRef || inUser) && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 1,
                          left: 1,
                          padding: '0 3px',
                          background: '#D97706',
                          color: '#fff',
                          fontSize: 9,
                          borderRadius: 3,
                          lineHeight: 1.5,
                        }}
                      >
                        {inRef ? '参考' : '输入'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </Image.PreviewGroup>
          )}
        </div>
      </Sider>

      <RequestPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onSubmit={onSubmit}
        submitting={submitting}
        runLabel={runLabel}
        prompt={prompt}
        referenceResources={refResources}
        userInputResources={userInputResources}
        models={perModelCost.map((p) => {
          const schema = p.model.inputSchema as ImageInputSchema;
          const inputs = perModelInputs[p.model.slug] ?? {};
          const params = schema.fields
            .filter((f) => f.type !== 'image_url' && f.type !== 'image_urls' && f.key !== 'prompt')
            .filter((f) => {
              const v = inputs[f.key];
              return v !== undefined && v !== null && v !== '';
            })
            .map((f) => ({ key: f.key, label: f.label, value: inputs[f.key] }));
          return {
            slug: p.model.slug,
            displayName: p.model.displayName,
            params,
            rmbCents: p.rmbCents,
          };
        })}
        totalRmbCents={totalRmbCents}
      />
    </Layout>
  );
};

const DropSlot: React.FC<{
  title: string;
  count: number;
  items: ResourceRow[];
  onDrop: (e: React.DragEvent) => void;
  onRemove: (id: number) => void;
}> = ({ title, count, items, onDrop, onRemove }) => {
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('is-over');
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('is-over');
  };
  return (
    <div
      className="drop-slot"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={onDrop}
    >
      <div className="drop-slot__header">
        <span>{title}</span>
        <Tag style={{ margin: 0 }}>{count}</Tag>
      </div>
      {items.length === 0 ? (
        <div
          style={{ fontSize: 11, color: '#94A3B8', padding: '8px 0', textAlign: 'center' }}
        >
          从右侧资源库拖拽图片到这里
        </div>
      ) : (
        <div className="drop-slot__grid">
          {items.map((r) => (
            <div key={r.id} className="drop-slot__item">
              <img src={thumb(r.image.accessUrl, 112)} alt="" />
              <div
                className="x"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(r.id);
                }}
              >
                ×
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ModelItem: React.FC<{
  model: ImageModelDTO;
  checked: boolean;
  inputs: Record<string, unknown>;
  prompt: string;
  exchangeRate: number;
  onCheck: (checked: boolean) => void;
  onChangeInput: (next: Record<string, unknown>) => void;
}> = ({ model, checked, inputs, prompt, exchangeRate, onCheck, onChangeInput }) => {
  const schema = model.inputSchema as ImageInputSchema;
  const effective = { ...inputs } as Record<string, unknown>;
  if (prompt && !('prompt' in effective)) effective.prompt = prompt;
  const cost = resolveImageCostWithDefaults(schema, model.pricing as ImagePricing, effective);
  const rmbCents = usdToRmbCentsCeil(cost.costUsd, exchangeRate);

  return (
    <div
      style={{
        border: checked ? '1.5px solid #D97706' : '1px solid #E7E2D6',
        borderRadius: 8,
        padding: 10,
        background: checked ? '#FEF9EC' : '#fff',
        transition: 'border-color .15s ease, background .15s ease',
      }}
    >
      <Checkbox checked={checked} onChange={(e) => onCheck(e.target.checked)}>
        <Space>
          <Typography.Text strong style={{ fontSize: 13 }}>
            {model.displayName}
          </Typography.Text>
          <Tooltip title={model.description}>
            <Tag color="gold" style={{ margin: 0 }}>
              {formatCents(rmbCents)}
            </Tag>
          </Tooltip>
        </Space>
      </Checkbox>
      {checked && (
        <div style={{ marginTop: 8, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {schema.fields
            .filter((f) => f.type !== 'image_url' && f.type !== 'image_urls' && f.key !== 'prompt')
            .map((field) => (
              <InlineField
                key={field.key}
                field={field}
                value={inputs[field.key]}
                onChange={(v) => onChangeInput({ ...inputs, [field.key]: v })}
              />
            ))}
        </div>
      )}
    </div>
  );
};

const InlineField: React.FC<{
  field: ImageInputField;
  value: unknown;
  onChange: (next: unknown) => void;
}> = ({ field, value, onChange }) => {
  if (field.type === 'enum') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography.Text style={{ fontSize: 12, minWidth: 56 }}>{field.label}</Typography.Text>
        <Select
          size="small"
          style={{ flex: 1 }}
          value={(value as string) ?? field.defaultValue}
          onChange={onChange}
          options={(field.enumValues ?? []).map((v) => ({ label: v, value: v }))}
        />
      </div>
    );
  }
  if (field.type === 'number') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography.Text style={{ fontSize: 12, minWidth: 56 }}>{field.label}</Typography.Text>
        <Input
          size="small"
          type="number"
          style={{ flex: 1 }}
          value={value as number | undefined}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Typography.Text style={{ fontSize: 12, minWidth: 56 }}>{field.label}</Typography.Text>
      <Input
        size="small"
        style={{ flex: 1 }}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

const STATUS_COLOR: Record<string, string> = {
  success: 'green',
  failed: 'red',
  timeout: 'orange',
  running: 'blue',
  pending: 'gold',
};

const STATUS_LABEL: Record<string, string> = {
  success: '已完成',
  failed: '失败',
  timeout: '超时',
  running: '生成中',
  pending: '排队中',
};

const ParamsPopover: React.FC<{ modelSlug: string; params: Record<string, unknown> }> = ({
  modelSlug,
  params,
}) => (
  <div style={{ maxWidth: 260 }}>
    <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>模型</div>
    <div style={{ fontWeight: 600, marginBottom: 8 }}>{modelSlug}</div>
    <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>参数</div>
    {Object.keys(params).length === 0 ? (
      <div style={{ color: '#94A3B8', fontSize: 12 }}>（默认）</div>
    ) : (
      <div style={{ fontSize: 12, lineHeight: 1.6 }}>
        {Object.entries(params).map(([k, v]) => (
          <div key={k}>
            <span style={{ color: '#94A3B8' }}>{k}:</span>{' '}
            <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const RecordCard: React.FC<{
  row: RecordRow;
  urlMap: Map<number, string>;
  resourceMap: Map<number, ResourceRow>;
  coverImageId: number | null;
  onDelete: () => void;
  onMarkCover: (imgId: number) => void;
}> = ({ row, urlMap, resourceMap, coverImageId, onDelete, onMarkCover }) => {
  // Display all inputs back combined (reference first, user input after) — same
  // order as the actual fal call.
  const inputResources = [...row.referenceResourceIds, ...row.userInputResourceIds]
    .map((id) => resourceMap.get(id))
    .filter(Boolean) as ResourceRow[];
  const isPending = row.status === 'pending' || row.status === 'running';
  const THUMB = 60;

  return (
    <div
      style={{
        padding: '8px 10px',
        borderRadius: 8,
        background: '#fff',
        border: '1px solid #F0EBDE',
      }}
    >
      {/* Line 1: runLabel + date + modelSlug + status + charge + delete */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          fontSize: 12,
          flexWrap: 'wrap',
        }}
      >
        <Typography.Text strong style={{ fontSize: 13 }}>
          {row.runLabel || '(无执行名)'}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {formatDate(row.createdAt)}
        </Typography.Text>
        <Tag color="purple" style={{ margin: 0 }}>
          {row.modelSlug}
        </Tag>
        <Tag
          color={STATUS_COLOR[row.status] ?? 'default'}
          icon={isPending ? <LoadingOutlined spin /> : undefined}
          style={{ margin: 0 }}
        >
          {STATUS_LABEL[row.status] ?? row.status}
        </Tag>
        {row.chargedCents != null && (
          <Tag style={{ margin: 0 }}>{formatCents(row.chargedCents)}</Tag>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <Popover
            content={<ParamsPopover modelSlug={row.modelSlug} params={row.inputsJson} />}
            placement="topRight"
            mouseEnterDelay={0.3}
          >
            <Button size="small" type="text" style={{ color: '#94A3B8' }}>
              参数
            </Button>
          </Popover>
          <Tooltip title="复制提示词">
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              style={{ color: '#94A3B8' }}
              disabled={!row.prompt}
              onClick={() => {
                if (!row.prompt) return;
                navigator.clipboard
                  .writeText(row.prompt)
                  .then(() => message.success('已复制提示词'))
                  .catch(() => message.error('复制失败'));
              }}
            />
          </Tooltip>
          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={onDelete} />
        </div>
      </div>

      {/* Line 2: input images | prompt | output images */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {inputResources.length === 0 ? (
            <EmptyTile size={THUMB}>无输入</EmptyTile>
          ) : (
            <Image.PreviewGroup>
              {inputResources.slice(0, 3).map((r) => (
                <div
                  key={r.id}
                  style={{
                    width: THUMB,
                    height: THUMB,
                    borderRadius: 6,
                    overflow: 'hidden',
                    border: '1px solid #EEE7D4',
                    flexShrink: 0,
                  }}
                >
                  <Image
                    src={thumb(r.image.accessUrl, 120)}
                    preview={{ src: r.image.accessUrl, mask: null }}
                    width={THUMB}
                    height={THUMB}
                    style={{ objectFit: 'cover', display: 'block' }}
                  />
                </div>
              ))}
              {inputResources.length > 3 && (
                <div
                  style={{
                    width: THUMB,
                    height: THUMB,
                    borderRadius: 6,
                    background: '#F8F5EC',
                    border: '1px solid #EEE7D4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748B',
                    fontSize: 12,
                    flexShrink: 0,
                  }}
                >
                  +{inputResources.length - 3}
                </div>
              )}
            </Image.PreviewGroup>
          )}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            lineHeight: 1.5,
            color: '#334155',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 4,
            overflow: 'hidden',
            alignSelf: 'center',
          }}
        >
          {row.errorMessage ? (
            <span style={{ color: '#DC2626' }}>{row.errorMessage}</span>
          ) : row.prompt ? (
            row.prompt
          ) : (
            <span style={{ color: '#94A3B8' }}>(无指令)</span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {row.outputImageIds.length === 0 ? (
            <EmptyTile size={THUMB}>{isPending ? '生成中' : '无输出'}</EmptyTile>
          ) : (
            <Image.PreviewGroup>
              {row.outputImageIds.map((imgId) => {
                const isCover = coverImageId === imgId;
                const url = urlMap.get(imgId);
                return (
                  <div
                    key={imgId}
                    className={`output-tile ${isCover ? 'is-cover' : ''}`}
                    style={{ width: THUMB, height: THUMB, flexShrink: 0 }}
                  >
                    {url ? (
                      <Image
                        src={thumb(url, 144)}
                        preview={{ src: url, mask: null }}
                        width={THUMB}
                        height={THUMB}
                        style={{ objectFit: 'cover', display: 'block' }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94A3B8',
                          fontSize: 10,
                        }}
                      >
                        #{imgId}
                      </div>
                    )}
                    <div className="output-tile__actions">
                      <Button
                        size="small"
                        shape="circle"
                        icon={
                          isCover ? <StarFilled style={{ color: '#D97706' }} /> : <StarOutlined />
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkCover(imgId);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </Image.PreviewGroup>
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyTile: React.FC<{ size: number; children: React.ReactNode }> = ({ size, children }) => (
  <div
    style={{
      width: size,
      height: size,
      border: '1px dashed #E7E2D6',
      borderRadius: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#94A3B8',
      fontSize: 10,
    }}
  >
    {children}
  </div>
);

export default ExperimentWorkbenchPage;
